import React from 'react';
import { Shield, Award, Users, Heart, Target, Plane } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { motion } from 'framer-motion';
import { FadeInWhenVisible, StaggerContainer, StaggerItem, CinematicCard } from '../components/CinematicEffects';

const AboutPage = () => {
  const values = [
    {
      icon: Shield,
      title: 'Seguridad Primero',
      description: 'La seguridad de nuestros pasajeros es nuestra prioridad número uno. Cumplimos con todas las normativas de aviación civil.'
    },
    {
      icon: Heart,
      title: 'Servicio Cercano',
      description: 'Tratamos a cada pasajero como parte de nuestra familia, brindando atención personalizada y calidez humana.'
    },
    {
      icon: Target,
      title: 'Puntualidad',
      description: 'Respetamos tu tiempo con un 95% de puntualidad en nuestros vuelos. Tu agenda es importante para nosotros.'
    },
    {
      icon: Award,
      title: 'Excelencia',
      description: 'Nos esforzamos continuamente por mejorar nuestros servicios y superar las expectativas de nuestros clientes.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section className="relative pt-32 pb-16 bg-gradient-to-br from-blue-900 via-blue-800 to-gray-900 text-white overflow-hidden">
        <div className="absolute inset-0 z-0">
          <div 
            className="ken-burns absolute inset-0"
            style={{
              backgroundImage: 'url(https://customer-assets.emergentagent.com/job_oaxaca-airways-dev/artifacts/y9a7e10n_PHOTO-2026-01-27-08-51-56%202.jpg)',
              backgroundSize: 'cover',
              backgroundPosition: 'center'
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-br from-blue-900/92 via-blue-800/88 to-gray-900/92"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Sobre Aerotucán
            </h1>
            <p className="text-xl text-blue-100 max-w-3xl mx-auto">
              25 años conectando las comunidades y destinos turísticos más importantes de Oaxaca
            </p>
          </motion.div>
        </div>
      </section>

      {/* Nuestra Historia */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <FadeInWhenVisible direction="left">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                  Nuestra Historia
                </h2>
                <div className="space-y-4 text-gray-700 text-lg leading-relaxed">
                  <motion.p
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: 0.2 }}
                  >
                    Aerotucán nació en 2001 con una visión clara: conectar las comunidades de Oaxaca 
                    mediante transporte aéreo accesible, seguro y eficiente. Con 25 años de experiencia, 
                    lo que comenzó como una pequeña operación con una sola aeronave, hoy se ha convertido 
                    en la aerolínea regional líder del estado.
                  </motion.p>
                  <motion.p
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: 0.4 }}
                  >
                    Con base en 5 de Mayo 1022A, Jalatlaco, Oaxaca, en el corazón del histórico Barrio de Jalatlaco, 
                    hemos crecido junto con nuestra comunidad. Cada vuelo que realizamos no es solo 
                    un trayecto entre dos puntos: es un puente que une familias, impulsa negocios 
                    y promueve el turismo en nuestra hermosa región.
                  </motion.p>
                  <motion.p
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: 0.6 }}
                  >
                    Hoy, con más de 15 años de experiencia, operamos rutas diarias que conectan 
                    la capital con los principales destinos turísticos y económicos de Oaxaca, 
                    manteniendo siempre nuestro compromiso con la seguridad, puntualidad y 
                    el trato humano que nos caracteriza.
                  </motion.p>
                </div>
              </div>
            </FadeInWhenVisible>

            <FadeInWhenVisible direction="right" delay={0.2}>
              <div className="relative">
                <div className="relative rounded-2xl overflow-hidden shadow-2xl image-zoom-container">
                  <img
                    src="https://customer-assets.emergentagent.com/job_oaxaca-airways-dev/artifacts/1a5qawb6_PHOTO-2026-01-27-08-51-56.jpg"
                    alt="Flota de Aerotucán"
                    className="image-zoom w-full h-full object-cover"
                  />
                </div>
                <motion.div 
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.5 }}
                  className="absolute -bottom-6 -right-6 bg-gradient-to-br from-amber-500 to-amber-600 text-white p-6 rounded-xl shadow-2xl hover-lift"
                >
                  <div className="text-4xl font-bold mb-1">15+</div>
                  <div className="text-sm">Años de experiencia</div>
                </motion.div>
              </div>
            </FadeInWhenVisible>
          </div>
        </div>
      </section>

      {/* Misión y Visión */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <StaggerContainer>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <StaggerItem>
                <CinematicCard className="border-0 shadow-xl h-full">
                  <CardContent className="p-10">
                    <motion.div 
                      whileHover={{ scale: 1.1, rotate: 5 }}
                      transition={{ duration: 0.3 }}
                      className="w-14 h-14 bg-gradient-to-br from-blue-900 to-blue-700 rounded-xl flex items-center justify-center mb-6"
                    >
                      <Target className="w-8 h-8 text-white" />
                    </motion.div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-4">Nuestra Misión</h3>
                    <p className="text-gray-700 text-lg leading-relaxed">
                      Conectar a las personas de Oaxaca con sus destinos mediante un servicio 
                      de transporte aéreo regional seguro, puntual y accesible, contribuyendo 
                      al desarrollo económico y turístico de nuestra región con un trato 
                      cercano y personalizado.
                    </p>
                  </CardContent>
                </CinematicCard>
              </StaggerItem>

              <StaggerItem>
                <CinematicCard className="border-0 shadow-xl h-full">
                  <CardContent className="p-10">
                    <motion.div
                      whileHover={{ scale: 1.1, rotate: -5 }}
                      transition={{ duration: 0.3 }}
                      className="w-14 h-14 bg-gradient-to-br from-amber-500 to-amber-600 rounded-xl flex items-center justify-center mb-6"
                    >
                      <Plane className="w-8 h-8 text-white" />
                    </motion.div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-4">Nuestra Visión</h3>
                    <p className="text-gray-700 text-lg leading-relaxed">
                      Ser la aerolínea regional preferida del sureste de México, reconocida 
                      por nuestra excelencia operacional, innovación en servicios y compromiso 
                      con las comunidades que servimos, expandiendo nuestra red de conectividad 
                      mientras mantenemos nuestros valores fundamentales.
                    </p>
                  </CardContent>
                </CinematicCard>
              </StaggerItem>
            </div>
          </StaggerContainer>
        </div>
      </section>

      {/* Nuestros Valores */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeInWhenVisible>
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Nuestros Valores
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Los principios que guían cada decisión y acción en Aerotucán
              </p>
            </div>
          </FadeInWhenVisible>

          <StaggerContainer>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {values.map((value, index) => {
                const Icon = value.icon;
                return (
                  <StaggerItem key={index}>
                    <CinematicCard className="border-0 shadow-lg text-center h-full">
                      <CardContent className="p-8">
                        <motion.div 
                          whileHover={{ scale: 1.1, rotate: 10 }}
                          transition={{ duration: 0.3 }}
                          className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-900 to-blue-700 rounded-xl mb-6"
                        >
                          <Icon className="w-8 h-8 text-white" />
                        </motion.div>
                        <h3 className="text-xl font-bold text-gray-900 mb-3">{value.title}</h3>
                        <p className="text-gray-600 leading-relaxed">{value.description}</p>
                      </CardContent>
                    </CinematicCard>
                  </StaggerItem>
                );
              })}
            </div>
          </StaggerContainer>
        </div>
      </section>

      {/* Compromiso con Oaxaca */}
      <section className="py-20 bg-gradient-to-br from-blue-50 to-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-blue-900 to-blue-700 rounded-2xl mb-6">
                <Users className="w-10 h-10 text-white" />
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Nuestro Compromiso con Oaxaca
              </h2>
            </div>

            <div className="bg-white rounded-2xl shadow-xl p-10">
              <div className="space-y-6 text-gray-700 text-lg leading-relaxed">
                <p>
                  Como empresa 100% oaxaqueña, nos sentimos profundamente comprometidos con 
                  el desarrollo de nuestro estado. No solo transportamos pasajeros; impulsamos 
                  el turismo, facilitamos negocios y conectamos familias.
                </p>
                <p>
                  Trabajamos de la mano con las comunidades locales, promovemos el empleo 
                  regional y apoyamos iniciativas que fortalecen la economía y cultura oaxaqueñas. 
                  Cada vuelo es una oportunidad para mostrar al mundo la belleza y riqueza de 
                  nuestra tierra.
                </p>
                <p>
                  Nuestro equipo está formado por profesionales oaxaqueños que conocen y aman 
                  esta región, lo que nos permite ofrecer un servicio con el carácter único 
                  y la calidez que caracteriza a nuestra gente.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Estadísticas */}
      <section className="py-20 bg-gradient-to-br from-blue-900 via-blue-800 to-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { number: '15+', label: 'Años de Experiencia' },
              { number: '50K+', label: 'Pasajeros Anuales' },
              { number: '4.8/5', label: 'Calificación Promedio' },
              { number: '95%', label: 'Puntualidad' }
            ].map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl md:text-5xl font-bold text-amber-400 mb-2">
                  {stat.number}
                </div>
                <div className="text-sm md:text-base text-blue-100">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutPage;
